import { Text as CodeMirrorText } from "@codemirror/state";
import { readTextFile, writeTextFile } from "@tauri-apps/plugin-fs";
import { Node as ProseMirrorNode } from "prosemirror-model";

import { parsePage, serializePage } from "@moment/api-collab/markdown";

import {
	exists,
	getDocumentFilePath,
	getPageDirPath,
	getPageFilePath,
	mkdir,
	readYamlFile,
	remove,
	writeYamlFile,
} from "~/data/filesystem";
import type { StoredPage } from "~/state/types/editors.types";

import type { DocumentMetadata } from "../types";

export const filesystemApi = {
	document: {
		metadata: {
			read: async (documentPath: string): Promise<DocumentMetadata | undefined> => {
				const filePath = await getDocumentFilePath(documentPath);

				if (!(await exists(filePath))) {
					return;
				}

				return readYamlFile<DocumentMetadata>(filePath);
			},
			write: async (documentPath: string, documentMetadata: DocumentMetadata) => {
				const filePath = await getDocumentFilePath(documentPath);

				await writeYamlFile(filePath, documentMetadata);
			},
		},

		delete: async (documentPath: string) => {
			await remove(documentPath);
		},
	},

	page: {
		contents: {
			read: async (documentPath: string, pageId: string) => {
				const raw = await readTextFile(await getPageFilePath(documentPath, pageId));

				// TODO: fix type returned from parsePage
				if (raw.trim() === "") {
					const emptyParsed: StoredPage = {
						doc: {
							type: "doc",
							content: [
								{
									type: "paragraph",
								},
							],
						},
						metadata: {
							id: pageId,
							slug: pageId,
							title: "",
							archived: false,
							parentPageID: null,
						},
					};

					return { parsed: emptyParsed, raw };
				}

				const parsed = (await parsePage(raw)) as StoredPage;

				return { parsed, raw };
			},

			write: async (
				documentPath: string,
				pageId: string,
				contents: ProseMirrorNode | CodeMirrorText
			): Promise<{ raw: string; parsed: JsonObject }> => {
				await mkdir(await getPageDirPath(documentPath, pageId));

				if (contents instanceof CodeMirrorText) {
					const raw = contents.toString();

					await writeTextFile(await getPageFilePath(documentPath, pageId), raw);

					return { raw, parsed: (await parsePage(raw)).doc };
				} else if (contents instanceof ProseMirrorNode) {
					const raw = serializePage(contents);

					await writeTextFile(await getPageFilePath(documentPath, pageId), raw);

					return { raw, parsed: contents.toJSON() };
				} else {
					throw new Error("Invalid contents");
				}
			},
		},
	},
};
